import { loadSession } from "./session";

/**
 * Thin client for the `/functions/v1/admin` Supabase edge function.
 *
 * - Single endpoint, dispatches on `action` field in the JSON body.
 * - Auth via `x-internal-token` header (the INTERNAL_SYNC_TOKEN).
 * - Tenant scoping is enforced server-side by joining on `public_key`.
 *
 * All UI code should go through this file rather than calling fetch directly,
 * so logging, error normalization, and (eventually) loading-state coordination
 * have a single place to live.
 */

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T = unknown>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const session = loadSession();
  if (!session) throw new ApiError(401, "Not signed in");

  // Two auth shapes:
  //   • Bearer mode  → Authorization: Bearer <token>, no body identity.
  //                    Edge function resolves the campground by hashing
  //                    the token and matching admin_token_hash.
  //   • Internal mode → x-internal-token: <INTERNAL_SYNC_TOKEN>, plus
  //                     public_key OR slug in the body so the function
  //                     knows which campground to act on.
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  let identity: Record<string, unknown> = {};
  if (session.mode === "bearer") {
    headers["Authorization"] = `Bearer ${session.token}`;
  } else {
    headers["x-internal-token"] = session.token;
    identity = session.publicKey
      ? { public_key: session.publicKey }
      : { slug: session.slug };
  }

  const res = await fetch(`${session.url}/functions/v1/admin`, {
    method: "POST",
    headers,
    body: JSON.stringify({ action, ...identity, ...body }),
  });

  let data: unknown = {};
  try { data = await res.json(); } catch { /* keep empty */ }

  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Typed response shapes for the actions the dashboard cares about.
// Keep these in lockstep with `supabase/functions/admin/index.ts`.
// ---------------------------------------------------------------------------

export interface WidgetConfig {
  color_primary?: string;
  position?: "right" | "left";
  title?: string;
  welcome_message?: string;
  suggested_questions?: string[];
  // Bot fallback when retrieval comes back empty.
  fallback_mode?: "contact" | "idk" | "custom";
  fallback_message?: string;
  // Visual extras for the widget customizer.
  initial_state?: "collapsed" | "open" | "proactive";
  proactive_message?: string;
  proactive_delay_ms?: number;
  locale?: string;
  avatar_url?: string;
  // Personality preset chosen by the owner (drives the home snapshot too).
  personality_preset?: "friendly" | "professional" | "concise" | "custom";
  // Ring buffer maintained server-side for the Domains debug list.
  recent_origin_rejections?: Array<{
    origin: string;
    first_seen_at: string;
    last_seen_at: string;
    count: number;
  }>;
}

export interface Campground {
  id: string;
  slug: string;
  name: string;
  public_key: string;
  allowed_domains: string[];
  system_prompt: string | null;
  widget_config: Record<string, unknown> & WidgetConfig;
  is_active: boolean;
  rate_limit_per_hour: number;
  chat_model?: string;
  temperature?: number;
  max_response_tokens?: number;
  retrieval_top_k?: number;
  retrieval_threshold?: number;
  notification_email?: string | null;
}

export interface HomeStats {
  conversations_this_week: number;
  conversations_all_time: number;
  assistant_messages_this_week: number;
  answered_well_pct: number | null;
  no_retrieval_pct: number | null;
  negative_feedback_pct: number | null;
  answered_well_pct_prev: number | null;
  no_retrieval_pct_prev: number | null;
  negative_feedback_pct_prev: number | null;
}

export interface ContentGap {
  question: string;
  occurrences: number;
  last_asked_at: string | null;
}

export interface RecentConversation {
  id: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
  first_user_message: string | null;
  first_assistant_message: string | null;
  assistant_feedback: "positive" | "negative" | null;
  assistant_grounded: boolean | null;
}

export interface LastSync {
  status: "pending" | "running" | "completed" | "failed";
  completed_at: string | null;
  documents_added: number;
  documents_updated: number;
}

export interface HomePayload {
  campground: Campground;
  stats: HomeStats;
  gaps: { items: ContentGap[]; total: number };
  open_tickets: number;
  recent_conversations: RecentConversation[];
  last_sync: LastSync | null;
}

export const getHome   = () => api<HomePayload>("get_home");

// ---------------------------------------------------------------------------
// Content gaps
// ---------------------------------------------------------------------------

/** Reasons the bot failed to fully answer a visitor question. */
export type GapReason = "no_retrieval" | "declined" | "negative_feedback";

export interface GapVariant {
  question: string;
  occurrences: number;
  last_asked_at: string | null;
  primary_reason?: GapReason | null;
  reasons?: GapReason[] | null;
}

export interface GapCluster {
  id: string;                  // canonical_question, used as a stable client key
  canonical_question: string;
  canonical_normalized: string;
  variants: GapVariant[];
  total_occurrences: number;
  last_asked_at: string | null;
  primary_reason: GapReason | null;
  reasons: GapReason[];
}

export interface GapsPayload {
  clusters: GapCluster[];
  total_clusters: number;
  total_questions: number;
  reason_breakdown?: Record<GapReason, number>;
}

export type ResolutionStatus = "answered" | "dismissed";
export type ResolutionFilter = "all" | ResolutionStatus;

export interface AnsweredGap {
  id: string;
  status: ResolutionStatus;
  original_question: string;
  canonical_question: string;
  variants: GapVariant[];
  occurrences_at_answer: number;
  document_id: string | null;
  created_at: string;
  documents?: { title: string; is_active: boolean } | null;
}

export interface AnsweredGapsPayload {
  items: AnsweredGap[];
  total: number;
}

export const listGaps          = () => api<GapsPayload>("list_gaps");
export const listAnsweredGaps  = (filter: ResolutionFilter = "all", limit = 50) =>
  api<AnsweredGapsPayload>("list_answered_gaps", { filter, limit });
// DEV-ONLY (disabled): seed_gaps inserted fake conversations so the gaps
// page was browsable during development. The server-side dispatch is also
// commented out, so re-enable both together if you ever need mock data.
// export const seedGaps          = () => api<{ inserted: number }>("seed_gaps");
export const answerGap         = (input: {
  title: string;
  answer: string;
  canonical_question: string;
  variants: GapVariant[];
}) => api<{ document: { id: string; title: string }; chunks_created: number }>("answer_gap", input);
export const dismissGap        = (input: {
  canonical_question: string;
  variants: GapVariant[];
}) => api<{ dismissed_gap: AnsweredGap }>("dismiss_gap", input);
export const reopenGap         = (gapId: string) =>
  api<{ ok: true }>("reopen_gap", { gap_id: gapId });

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export type ConversationFilter = "all" | "negative" | "no_retrieval";

export interface ConversationListItem {
  id: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
  visitor_metadata: Record<string, unknown> | null;
  first_user_message: string | null;
  last_assistant_message: string | null;
  has_negative_feedback: boolean;
  has_no_retrieval: boolean;
}

export interface ConversationsListPayload {
  items: ConversationListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationCitation {
  document_id: string;
  title: string;
  source_type: string;
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  feedback: "positive" | "negative" | null;
  feedback_at: string | null;
  feedback_comment: string | null;
  grounded: boolean;
  model_used: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  latency_ms: number | null;
  citations: ConversationCitation[];
}

export interface ConversationDetail {
  conversation: {
    id: string;
    session_id: string;
    started_at: string;
    last_message_at: string;
    message_count: number;
    visitor_metadata: Record<string, unknown> | null;
  };
  messages: ConversationMessage[];
}

export const listConversations = (input?: {
  limit?: number;
  offset?: number;
  filter?: ConversationFilter;
  search?: string;
}) =>
  api<ConversationsListPayload>("list_conversations", {
    limit:  input?.limit  ?? 25,
    offset: input?.offset ?? 0,
    filter: input?.filter ?? "all",
    search: input?.search ?? "",
  });

export const getConversation = (conversationId: string) =>
  api<ConversationDetail>("get_conversation", { conversation_id: conversationId });

// ---------------------------------------------------------------------------
// Knowledge base (owner-authored documents)
// ---------------------------------------------------------------------------

export type KnowledgeSourceType =
  | "rates" | "rules" | "amenities" | "policies" | "cancellation_policy"
  | "pet_policy" | "check_in_info" | "reservation_info" | "description"
  | "location" | "faq" | "other";

export interface KnowledgeDoc {
  id: string;
  title: string;
  source_type: KnowledgeSourceType;
  source_url: string | null;
  raw_content: string;
  is_active: boolean;
  is_manual: boolean;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeListPayload {
  items: KnowledgeDoc[];
  total: number;
}

export const listKnowledge = (opts: { limit?: number; offset?: number; onlyManual?: boolean } = {}) =>
  api<KnowledgeListPayload>("list_knowledge", {
    limit:       opts.limit  ?? 200,
    offset:      opts.offset ?? 0,
    only_manual: opts.onlyManual ?? false,
  });

export const addKnowledge = (input: {
  title: string;
  content: string;
  source_type: KnowledgeSourceType;
}) =>
  api<{ document: { id: string; title: string; source_type: string }; chunks_created: number }>(
    "add_knowledge",
    input,
  );

export const deleteKnowledge = (documentId: string) =>
  api<{ ok: true }>("delete_knowledge", { document_id: documentId });

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export interface UpdateSettingsInput {
  system_prompt?: string | null;
  allowed_domains?: string[];
  widget_config?: WidgetConfig & Record<string, unknown>;
  temperature?: number;
  retrieval_top_k?: number;
  retrieval_threshold?: number;
  max_response_tokens?: number;
  chat_model?: string;
  notification_email?: string | null;
}

export const updateSettings = (input: UpdateSettingsInput) =>
  api<{ campground: Campground }>("update_settings", input as Record<string, unknown>);

export const getOverview = () =>
  api<{ campground: Campground; counts: Record<string, number> }>("get_overview");

// ---------------------------------------------------------------------------
// Avatar upload
// ---------------------------------------------------------------------------

export interface UploadAvatarResponse {
  url: string;
  path: string;
  size: number;
  mime: string;
}

/**
 * Read a File as a base64 string and POST it to `upload_avatar`. The
 * edge function writes to the public `widget-assets` bucket and returns
 * the public URL the caller should persist into
 * `widget_config.avatar_url`.
 */
export async function uploadAvatar(file: File): Promise<UploadAvatarResponse> {
  const base64 = await fileToBase64(file);
  return api<UploadAvatarResponse>("upload_avatar", {
    mime: file.type,
    content_base64: base64,
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") return reject(new Error("Unexpected reader result"));
      // strip "data:<mime>;base64," so the server gets a clean payload
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export interface SandboxChatInput {
  message: string;
  system_prompt?: string;
  temperature?: number;
  retrieval_top_k?: number;
  retrieval_threshold?: number;
}

export interface SandboxChatResponse {
  answer: string;
  citations: Array<{
    document_id: string;
    title: string;
    source_type: string;
    similarity: number;
  }>;
  grounded: boolean;
  model_used: string;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
}

export const sandboxChat = (input: SandboxChatInput) =>
  api<SandboxChatResponse>("sandbox_chat", input as unknown as Record<string, unknown>);

export interface SyncSummary {
  status: string;
  documents_added: number;
  documents_updated: number;
  documents_removed: number;
  documents_unchanged: number;
}

/**
 * Pull the campground's latest content from Roverpass. Works in bearer mode:
 * the admin edge function proxies the call to the `sync` function with the
 * internal token server-side, scoped to the tenant resolved from the bearer
 * token. The dashboard never handles INTERNAL_SYNC_TOKEN.
 */
export const syncNow = () => api<SyncSummary>("proxy_sync");

// ---------------------------------------------------------------------------
// Tickets (support messages raised by campers from the widget)
// ---------------------------------------------------------------------------

export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketCategory = "reservation" | "complaint" | "question" | "other";
// "unresolved" is a server-side convenience filter (open + in_progress).
export type TicketFilter = "all" | "unresolved" | TicketStatus;

export interface Ticket {
  id: string;
  reference: string;
  status: TicketStatus;
  category: TicketCategory;
  visitor_name: string;
  visitor_email: string;
  subject: string;
  message: string;
  admin_notes: string | null;
  conversation_id: string | null;
  visitor_metadata: Record<string, unknown> | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TicketsListPayload {
  items: Ticket[];
  total: number;
}

export const listTickets = (opts: { status?: TicketFilter; limit?: number; offset?: number } = {}) =>
  api<TicketsListPayload>("list_tickets", {
    status: opts.status ?? "all",
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  });

/**
 * Update a ticket. Any combination of: change status, edit owner notes, and
 * send a reply email to the camper. Returns the updated row plus whether the
 * reply email actually went out.
 */
export const updateTicket = (input: {
  ticket_id: string;
  status?: TicketStatus;
  admin_notes?: string;
  reply?: string;
}) => api<{ ticket: Ticket; emailed: boolean; email_error?: string }>("update_ticket", input);
